import React, { useEffect, useState } from "react";

type TaskKey = "hoverTitle" | "clickStar" | "clickSocial" | "openBox";

const STORAGE_KEY = "reveal-quest-state";

const TASKS: { key: TaskKey; label: string; hint?: string }[] = [
  {
    key: "hoverTitle",
    label: "Whisper to the title",
    hint: "Hover the main title for 2s",
  },
  {
    key: "clickStar",
    label: "Find the hidden star",
    hint: "There's a tiny star hiding in this box",
  },
  {
    key: "clickSocial",
    label: "Wave at my socials",
    hint: "Click any social icon",
  },
  {
    key: "openBox",
    label: "Open the mystery box",
    hint: "Click the box to reveal a secret",
  },
];

export default function RevealQuest(): React.ReactElement {
  const [completed, setCompleted] = useState<Record<TaskKey, boolean>>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw
        ? (JSON.parse(raw) as Record<TaskKey, boolean>)
        : ({} as Record<TaskKey, boolean>);
    } catch {
      return {} as Record<TaskKey, boolean>;
    }
  });

  useEffect(() => {
    const onHover = () => setCompleted((s) => ({ ...s, hoverTitle: true }));
    const onSocial = () => setCompleted((s) => ({ ...s, clickSocial: true }));
    const onBox = () => setCompleted((s) => ({ ...s, openBox: true }));

    function onGlobalHover() {
      onHover();
    }

    window.addEventListener(
      "revealQuest:hoverTitle",
      onGlobalHover as EventListener,
    );
    window.addEventListener(
      "revealQuest:clickSocial",
      onSocial as EventListener,
    );
    window.addEventListener("revealQuest:openBox", onBox as EventListener);

    return () => {
      window.removeEventListener(
        "revealQuest:hoverTitle",
        onGlobalHover as EventListener,
      );
      window.removeEventListener(
        "revealQuest:clickSocial",
        onSocial as EventListener,
      );
      window.removeEventListener("revealQuest:openBox", onBox as EventListener);
    };
  }, []);

  // local star click handled inside component
  function handleStarClick() {
    setCompleted((s) => ({ ...s, clickStar: true }));
  }

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
    } catch {
      // ignore
    }
  }, [completed]);

  const allDone = TASKS.every((t) => completed[t.key]);

  return (
    <div className="p-4 rounded-lg border border-border bg-bg-elevated">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-mono uppercase tracking-wider text-text-muted">
          Reveal Quest
        </h4>
        <div className="text-xs text-text-muted">
          Complete the mini-tasks to unlock
        </div>
      </div>

      <div className="grid gap-2 mb-3">
        {TASKS.map((t) => (
          <div
            key={t.key}
            className="flex items-center justify-between gap-3 p-2 rounded border border-border bg-bg-surface"
          >
            <div>
              <div className="text-sm font-medium">{t.label}</div>
              {t.hint && (
                <div className="text-xs text-text-muted">{t.hint}</div>
              )}
            </div>
            <div className="text-sm">{completed[t.key] ? "✅" : "—"}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative">
          <button
            type="button"
            onClick={handleStarClick}
            aria-pressed={!!completed.clickStar}
            className="px-3 py-2 rounded bg-accent text-white"
          >
            Find Star
          </button>
          <div className="absolute -top-3 -right-6 text-yellow-300">✨</div>
        </div>

        <div>
          <button
            type="button"
            onClick={() =>
              window.dispatchEvent(new CustomEvent("revealQuest:openBox"))
            }
            className="px-3 py-2 rounded border border-border bg-bg-elevated"
          >
            Mystery Box
          </button>
        </div>

        <div className="ml-auto text-sm font-medium">
          {allDone ? "Secret Unlocked! 🎉" : "Keep going..."}
        </div>
      </div>

      {allDone && (
        <div className="mt-3 p-3 rounded bg-gradient-to-r from-accent/10 to-accent/5 border border-accent">
          <div className="font-bold">Hidden Case Study</div>
          <div className="text-sm text-text-muted">
            You found the secret — here's a short case study about my AI-native
            analytics work.
          </div>
        </div>
      )}
    </div>
  );
}
